/**
 * Orte: suchen, ansehen, eigene Namen vergeben.
 *
 * Google gibt nur Place-IDs heraus; die Namen stammen aus Nominatim. Eine
 * eigene Benennung schlägt beide und überlebt jede Neuauflösung — „Zuhause“
 * steht in keinem Kartenwerk.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, MapPin, Pencil, Search, X } from "lucide-react";

import { api, ApiFehler, type OrtTreffer } from "@/api.ts";
import { datumLokal } from "@/zeit.ts";

export function OrteSeite() {
  const [felder, setFelder] = useSearchParams();
  const suchwort = felder.get("q") ?? "";

  const [eingabe, setEingabe] = useState(suchwort);
  const [orte, setOrte] = useState<OrtTreffer[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bearbeitet, setBearbeitet] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState("");

  // Eingabe und Adresszeile auseinanderhalten: getippt wird sofort, gesucht
  // erst nach einer kurzen Pause.
  useEffect(() => {
    const zeiger = setTimeout(() => {
      if (eingabe !== suchwort) {
        setFelder(eingabe ? { q: eingabe } : {}, { replace: true });
      }
    }, 300);
    return () => clearTimeout(zeiger);
  }, [eingabe, suchwort, setFelder]);

  useEffect(() => {
    let verworfen = false;
    setLaedt(true);
    setFehler(null);

    api
      .orte(suchwort)
      .then((treffer) => {
        if (!verworfen) setOrte(treffer);
      })
      .catch((e: unknown) => {
        if (!verworfen) setFehler(e instanceof ApiFehler ? e.message : String(e));
      })
      .finally(() => {
        if (!verworfen) setLaedt(false);
      });

    return () => {
      verworfen = true;
    };
  }, [suchwort]);

  const benennen = async (placeId: string) => {
    try {
      const aktualisiert = await api.ortBenennen(placeId, entwurf);
      setOrte((bisher) =>
        bisher.map((o) => (o.placeId === placeId ? { ...o, name: aktualisiert.name } : o)),
      );
      setBearbeitet(null);
    } catch (e: unknown) {
      setFehler(e instanceof ApiFehler ? e.message : String(e));
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border bg-surface px-4 py-3 pl-16 lg:pl-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-text-muted" />
          <input
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
            placeholder="Ort, Straße oder Stadt suchen…"
            className="w-full rounded-lg border border-border bg-surface py-2 pr-3 pl-9 text-sm"
          />
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {laedt && <p className="text-sm text-text-muted">wird geladen…</p>}
        {fehler && <p className="text-sm text-danger">{fehler}</p>}
        {!laedt && !fehler && orte.length === 0 && (
          <p className="text-sm text-text-muted">
            {suchwort ? `Nichts zu „${suchwort}“ gefunden.` : "Noch keine Orte im Archiv."}
          </p>
        )}

        <ul className="space-y-2">
          {orte.map((ort) => (
            <li
              key={ort.placeId}
              className="rounded-lg border border-border bg-surface px-4 py-3"
            >
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-primary" />

                <div className="min-w-0 flex-1">
                  {bearbeitet === ort.placeId ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={entwurf}
                        onChange={(e) => setEntwurf(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void benennen(ort.placeId);
                          if (e.key === "Escape") setBearbeitet(null);
                        }}
                        placeholder="eigener Name, leer lassen zum Zurücksetzen"
                        className="min-w-0 flex-1 rounded border border-border bg-surface px-2 py-1 text-sm"
                      />
                      <button
                        onClick={() => void benennen(ort.placeId)}
                        className="rounded p-1.5 text-success hover:bg-page-bg"
                        title="Übernehmen"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => setBearbeitet(null)}
                        className="rounded p-1.5 text-text-muted hover:bg-page-bg"
                        title="Abbrechen"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {ort.name ?? <span className="text-text-muted">noch ohne Namen</span>}
                      </span>
                      <button
                        onClick={() => {
                          setBearbeitet(ort.placeId);
                          setEntwurf(ort.name ?? "");
                        }}
                        className="rounded p-1 text-text-muted hover:bg-page-bg"
                        title="Eigenen Namen vergeben"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  )}

                  <p className="mt-1 text-xs text-text-muted">
                    {ort.besuche} {ort.besuche === 1 ? "Besuch" : "Besuche"}
                    {ort.erstmals && ` · seit ${datumLokal(ort.erstmals)}`}
                    {ort.zuletzt && ` · zuletzt ${datumLokal(ort.zuletzt)}`}
                    {ort.semantikTyp && ort.semantikTyp !== "Unknown" && ` · ${ort.semantikTyp}`}
                  </p>
                </div>

                {ort.lat !== null && ort.lon !== null && (
                  <span className="shrink-0 font-mono text-xs text-text-muted">
                    {ort.lat.toFixed(4)}, {ort.lon.toFixed(4)}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
